/* No inferred anthropometric thresholds. All fit ranges must be supplied and verified per product. */
(function (root, factory) {
  const engine = factory();
  if (typeof module === 'object' && module.exports) module.exports = engine;
  else root.AestraFitEngine = engine;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const positive = n => typeof n === 'number' && Number.isFinite(n) && n > 0;
  const range = r => Array.isArray(r) && r.length === 2 && r.every(positive) && r[0] <= r[1];
  const within = (n, r) => n >= r[0] && n <= r[1];
  const text = s => typeof s === 'string' && s.trim().length > 0;
  function measurements(row, category) {
    const m = row && row.garment;
    if (!m || row.verified !== true || !text(row.source) || !positive(m.length_cm)) return false;
    if (!['outseam', 'back-length'].includes(m.length_basis)) return false;
    if (!['flat', 'circumference'].includes(m.waist_basis) || !positive(m.waist_relaxed_cm) || !positive(m.waist_stretched_cm) || m.waist_stretched_cm < m.waist_relaxed_cm) return false;
    if (category === 'pants' && (m.length_basis !== 'outseam' || !positive(m.hem_flat_cm))) return false;
    return true;
  }
  function validProfile(p, row, c) {
    return p && ['relaxed', 'extra-baggy'].includes(p.fit) && p.verified === true && text(p.source) &&
      Array.isArray(p.evidence_refs) && p.evidence_refs.length > 0 && p.evidence_refs.every(text) &&
      range(p.height_cm) && range(p.weight_kg) && range(p.body_waist_cm) && range(p.garment_length_cm) &&
      p.body_waist_cm[1] <= row.garment.waist_stretched_cm * (row.garment.waist_basis === 'flat' ? 2 : 1) &&
      within(row.garment.length_cm, p.garment_length_cm) &&
      (c.category !== 'pants' || ['above-shoe', 'on-shoe', 'stacked'].includes(p.hem));
  }
  function validate(c, handle, sizes) {
    const issues = [];
    if (!c || c.version !== 1 || c.product_handle !== handle || c.enabled !== true || c.measurement_scope !== 'all_variants')
      return ['Verified product-specific sizing configuration'];
    if (!['pants', 'other'].includes(c.category)) issues.push('Garment category');
    if (!Array.isArray(c.sizes) || c.sizes.length === 0) return ['Verified garment measurements and fit ranges for each size'];
    if (c.sizes.some(s => !s || typeof s !== 'object' || !text(s.label))) return ['Valid size records'];
    if (new Set(c.sizes.map(s => s.label)).size !== c.sizes.length || c.sizes.some(s => !sizes.includes(s.label))) issues.push('Unique size labels matching the product');
    for (const label of sizes) {
      const row = c.sizes.find(s => s.label === label);
      if (!measurements(row, c.category)) { issues.push(`${label}: verified garment waist and length measurements${c.category === 'pants' ? ' and flat hem width' : ''}`); continue; }
      if (c.mode === 'height-only') {
        if (!range(row.brand_height_cm)) issues.push(`${label}: merchant height guide`);
      } else if (!Array.isArray(row.profiles) || !row.profiles.length || row.profiles.some(p => !validProfile(p, row, c)))
        issues.push(`${label}: verified body ranges, length suitability and fit evidence`);
    }
    return issues;
  }
  function available(data, options, size) {
    return data.variants.some(v => v.available && v.options.every((o, i) => o === (i === data.sizeIndex ? size : options[i])));
  }
  function recommend(c, handle, sizes, input, stock) {
    if (!positive(input.height) || (c?.mode !== 'height-only' && (!positive(input.weight) || (input.waist !== null && !positive(input.waist)) || !['relaxed', 'extra-baggy'].includes(input.fit)))) return { status: 'invalid' };
    const missing = validate(c, handle, sizes);
    if (missing.length) return { status: 'missing', missing };
    if (c.mode === 'height-only') {
      const matches = c.sizes.flatMap((row, index) => within(input.height, row.brand_height_cm) ? [{row, index, profiles: [], available: stock(row.label)}] : []);
      if (!matches.length) return {status: 'no-match', matches, heightOnly: true};
      const selected = matches.find(m => m.available);
      return {status: selected ? 'match' : 'unavailable', selected, matches, heightOnly: true};
    }
    const matches = c.sizes.flatMap((row, index) => {
      const profiles = row.profiles.filter(p => p.fit === input.fit && within(input.height, p.height_cm) && within(input.weight, p.weight_kg) && (input.waist === null || within(input.waist, p.body_waist_cm)));
      return profiles.length ? [{ row, index, profiles, available: stock(row.label) }] : [];
    });
    const inStock = matches.filter(m => m.available);
    if (!matches.length) return { status: 'no-match', matches: [] };
    if (!inStock.length) return { status: 'unavailable', matches };
    // Merchant supplies smallest-to-largest order. Within overlapping verified ranges,
    // relaxed uses the smaller suitable size; extra baggy uses the larger suitable size.
    const selected = input.fit === 'extra-baggy' ? inStock[inStock.length - 1] : inStock[0];
    const alternative = inStock.find(m => m !== selected && Math.abs(m.index - selected.index) === 1);
    return { status: 'match', selected, alternative, matches, waistProvided: input.waist !== null };
  }
  return { validate, recommend, available, measurements };
});
